// AnnouncementsList.js
import { useState, useEffect } from "react";
import ListControls from "../ListControls/ListControls";
import {
  getAnnouncementsList,
  createAnnouncement,
  cancelAnnouncement,
} from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./Announcements.css";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";

const AnnouncementsList = () => {
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    targetUserType: "customers",
    targetCity: "",
    targetBehavior: "",
    scheduledAt: "",
  });

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await getAnnouncementsList({ sortBy, sortOrder });
      setAnnouncements(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [sortBy, sortOrder]);

  const openCreateModal = () => {
    setFormData({
      title: "",
      body: "",
      targetUserType: "customers",
      targetCity: "",
      targetBehavior: "",
      scheduledAt: "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createAnnouncement(formData);
      setModalOpen(false);
      fetchAnnouncements();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to send announcement.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this announcement?")) return;
    try {
      await cancelAnnouncement({ id });
      fetchAnnouncements();
    } catch (err) {
      alert(err.response?.data?.message || "Cancel failed.");
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      sent: "status-completed",
      pending: "status-pending",
      cancelled: "status-failed",
    };
    return map[status] || "";
  };

  return (
    <div className="announcements-page">
      <ListControls sortOptions={[{value:"createdAt",label:"Created date"},{value:"title",label:"Title"},{value:"status",label:"Status"},{value:"scheduledAt",label:"Scheduled date"}]} sortBy={sortBy} sortOrder={sortOrder} onSortByChange={setSortBy} onSortOrderChange={setSortOrder} />
      <div className="page-actions">
        <button className="btn-primary" onClick={openCreateModal}>
          <AddIcon /> Send Announcement
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table className="data_table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Body</th>
                <th>Target</th>
                <th>Scheduled</th>
                <th>Status</th>
                <th>Sent Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No announcements.
                  </td>
                </tr>
              ) : (
                announcements.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <strong>{a.title}</strong>
                    </td>
                    <td>{a.body}</td>
                    <td>
                      {a.targetUserType}
                      {a.targetCity && `, ${a.targetCity}`}
                      {a.targetBehavior && `, ${a.targetBehavior}`}
                    </td>
                    <td>
                      {a.scheduledAt
                        ? new Date(a.scheduledAt).toLocaleString()
                        : "Immediate"}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${getStatusBadge(a.status)}`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td>{a.sentCount || 0}</td>
                    <td>
                      <div className="action_buttons">
                        {a.status === "pending" && (
                          <CancelIcon
                            fontSize="small"
                            onClick={() => handleCancel(a.id)}
                          />
                        )}
                        {a.status !== "pending" && (
                          <span className="no-action">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Send Push Notification"
      >
        <div className="announcement-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g. Free Delivery Weekend"
            />
          </div>
          <div className="form-group">
            <label>Message Body *</label>
            <textarea
              value={formData.body}
              onChange={(e) =>
                setFormData({ ...formData, body: e.target.value })
              }
              placeholder="Your message here..."
              rows="3"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Target User Type</label>
              <select
                value={formData.targetUserType}
                onChange={(e) =>
                  setFormData({ ...formData, targetUserType: e.target.value })
                }
              >
                <option value="customers">Customers</option>
                <option value="drivers">Drivers</option>
                <option value="vendors">Vendors</option>
                <option value="all">All</option>
              </select>
            </div>
            <div className="form-group">
              <label>Target City</label>
              <input
                type="text"
                value={formData.targetCity}
                onChange={(e) =>
                  setFormData({ ...formData, targetCity: e.target.value })
                }
                placeholder="e.g. New York"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Target Behavior</label>
              <select
                value={formData.targetBehavior}
                onChange={(e) =>
                  setFormData({ ...formData, targetBehavior: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="new">New</option>
              </select>
            </div>
            <div className="form-group">
              <label>Scheduled At (optional)</label>
              <input
                type="datetime-local"
                value={formData.scheduledAt}
                onChange={(e) =>
                  setFormData({ ...formData, scheduledAt: e.target.value })
                }
              />
            </div>
          </div>
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                "Sending..."
              ) : (
                <>
                  <SendIcon fontSize="small" /> Send
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AnnouncementsList;
